import { endpoints } from '@api'
import { useToast } from '@chakra-ui/react'
import type { SessionTranscriptApiResponse } from '@handlers/sessions/types'
import React, {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getSessionControllerGetProgressNotesForSessionQueryKey,
  useSessionControllerGetCompletedSessionDetailsV2,
  useSessionControllerGetProgressNotesForSession,
  useSessionControllerLockNote
} from '~/clinician-api'
import type {
  CompletedSessionDtoV2,
  SessionProgressNoteDto,
  SessionProgressNotesDto
} from '~/clinician-api/models'

interface SessionManagementContextType {
  // Note Management
  selectedProgressNoteId: string | undefined
  setSelectedProgressNoteId: (id: string | undefined) => void
  handleLockNote: () => Promise<void>
  isLockingNote: boolean

  // Progress Note Data
  progressNoteData: SessionProgressNotesDto | undefined
  isProgressNotesLoading: boolean
  progressNote: SessionProgressNoteDto
  isAnyNoteLoading: boolean | undefined
  isNoteLocked: boolean

  // Session Data
  session: CompletedSessionDtoV2 | undefined
  isSessionLoading: boolean
  refetchSession: () => void

  // Transcript Data
  sessionTranscriptData: SessionTranscriptApiResponse | undefined
  sessionTranscriptLoading: boolean

  // Note Generation
  generateProgressNote: (params: {
    sessionId: string
    noteType: string
  }) => Promise<any>
  isGeneratingProgressNote: boolean

  // Metadata
  sessionId: string
}

interface SessionManagementProviderProps {
  children: ReactNode
  sessionId: string
}

const SessionManagementContext = createContext<
  SessionManagementContextType | undefined
>(undefined)

export const SessionManagementProvider: FC<SessionManagementProviderProps> = ({
  children,
  sessionId
}) => {
  const queryClient = useQueryClient()
  const toast = useToast()

  // ========================================================================
  // PROGRESS NOTES
  // ========================================================================

  const [selectedProgressNoteId, setSelectedProgressNoteId] = useState<
    string | undefined
  >(undefined)
  const [isLockingNote, setIsLockingNote] = useState(false)

  const { data: progressNoteData, isLoading: isProgressNotesLoading } =
    useSessionControllerGetProgressNotesForSession(sessionId, {
      query: {
        enabled: !!sessionId,
        refetchInterval: (data: SessionProgressNotesDto | undefined) => {
          if (
            data?.sessionProgressNotes.some(
              (pn: SessionProgressNoteDto) => pn.note?.isLoading
            )
          ) {
            return 5000
          }

          return false
        }
      }
    })

  const sessionProgressNotes = progressNoteData?.sessionProgressNotes || []

  const isAnyNoteLoading = useMemo(
    () =>
      sessionProgressNotes?.some(
        (pn: SessionProgressNoteDto) => pn.note?.isLoading
      ),
    [sessionProgressNotes]
  )

  const progressNote = useMemo(
    () =>
      sessionProgressNotes?.find(
        (pn: SessionProgressNoteDto) => pn.id === selectedProgressNoteId
      ) || sessionProgressNotes[0],
    [sessionProgressNotes, selectedProgressNoteId, setSelectedProgressNoteId]
  )

  const isNoteLocked = useMemo(() => {
    return !!sessionProgressNotes?.find(
      (pn: SessionProgressNoteDto) => pn.id === selectedProgressNoteId
    )?.lockedAt
  }, [sessionProgressNotes, selectedProgressNoteId])

  // Set selectedProgressNoteId to locked note or first note when data loads and no note is selected
  useEffect(() => {
    if (
      sessionProgressNotes?.length &&
      sessionProgressNotes?.length > 0 &&
      !selectedProgressNoteId &&
      !isAnyNoteLoading
    ) {
      const lockedNote = sessionProgressNotes?.find(
        (pn: SessionProgressNoteDto) => pn.lockedAt
      )
      setSelectedProgressNoteId(lockedNote?.id || sessionProgressNotes[0].id)
    }
  }, [sessionProgressNotes, selectedProgressNoteId, isAnyNoteLoading])

  // Set selectedProgressNoteId to loading note when any note is loading
  useEffect(() => {
    if (isAnyNoteLoading) {
      const loadingNote = sessionProgressNotes?.find(
        (pn: SessionProgressNoteDto) => pn.note?.isLoading
      )
      setSelectedProgressNoteId(loadingNote?.id)
    }
  }, [isAnyNoteLoading, sessionProgressNotes])

  // ========================================================================
  // SESSION DATA
  // ========================================================================

  const {
    data: session,
    isLoading: isSessionLoading,
    refetch: refetchSession
  } = useSessionControllerGetCompletedSessionDetailsV2(sessionId, {
    query: { enabled: !!sessionId }
  })

  // ========================================================================
  // TRANSCRIPT DATA
  // ========================================================================

  // We only need to know whether a transcript record exists to determine UI states
  // (e.g., enabling "Change note type" or "Note preferences"). The transcript
  // modal calls `useSessionTranscript` with this initialData so the full
  // transcript text is fetched lazily, when the modal is actually opened.
  const { data: sessionTranscriptData, isLoading: sessionTranscriptLoading } =
    useQuery<SessionTranscriptApiResponse>(
      [endpoints.getSessionTranscript.getCacheId(), sessionId],
      () =>
        endpoints.getSessionTranscript.request({
          sessionId
        }) as Promise<SessionTranscriptApiResponse>,
      {
        enabled: !!sessionId
      }
    )

  // ========================================================================
  // NOTE GENERATION
  // ========================================================================

  const {
    mutateAsync: generateProgressNote,
    isLoading: isGeneratingProgressNote
  } = useMutation(endpoints.generateProgressNote.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
      )
    }
  })

  // Track note generation completion
  useEffect(() => {
    const isFirstNote = selectedProgressNoteId === sessionProgressNotes[0]?.id
    if (selectedProgressNoteId && !isAnyNoteLoading && isFirstNote) {
      // eslint-disable-next-line
      parent.postMessage({ type: 'BP_NOTE_GENERATED', sessionId }, '*')
    }
  }, [
    isAnyNoteLoading,
    selectedProgressNoteId,
    sessionProgressNotes,
    sessionId
  ])

  // ========================================================================
  // NOTE LOCKING
  // ========================================================================

  const { mutateAsync: lockNote } = useSessionControllerLockNote()

  const handleLockNote = useCallback(async () => {
    if (!selectedProgressNoteId || !sessionId) {
      return
    }

    setIsLockingNote(true)
    try {
      await lockNote({
        id: sessionId,
        noteId: selectedProgressNoteId
      })

      // Refresh progress notes so UI reflects newly locked state
      await queryClient.invalidateQueries(
        getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
      )

      toast({
        title: 'Note locked',
        description: 'The note has been locked.',
        status: 'success',
        duration: 1500
      })
    } catch (_error) {
      toast({
        title: 'Error locking note',
        description: 'There was a problem locking the note. Please try again.',
        status: 'error',
        duration: 1500
      })
    } finally {
      setIsLockingNote(false)
    }
  }, [selectedProgressNoteId, sessionId, lockNote, queryClient, toast])

  return (
    <SessionManagementContext.Provider
      value={{
        selectedProgressNoteId,
        setSelectedProgressNoteId,
        handleLockNote,
        isLockingNote,
        progressNoteData,
        isProgressNotesLoading,
        progressNote,
        isAnyNoteLoading,
        session,
        isSessionLoading,
        refetchSession,
        sessionTranscriptData,
        sessionTranscriptLoading,
        generateProgressNote,
        isGeneratingProgressNote,
        sessionId,
        isNoteLocked
      }}
    >
      {children}
    </SessionManagementContext.Provider>
  )
}

export const useSessionManagement = (): SessionManagementContextType => {
  const context = useContext(SessionManagementContext)
  if (context === undefined) {
    throw new Error(
      'useSessionManagement must be used within a SessionManagementProvider'
    )
  }
  return context
}
